import type { ConnectProto } from "@racoon/api";

// The order bot (go-backend `order_service.go`) stores what Meta draws on the phone as
// placeholders: the Flow template it opens a dialogue with, and the client's completed form.
const FLOW_TEMPLATE_BODY = "[template waste_pickup_order_flow]";
const FLOW_REPLY_PREFIX = "[flow] ";

/** The bot's side of the dialogue; every other message is the client's. */
export const isOutgoing = (msg: Pick<ConnectProto.WhatsappMessage, "direction">) =>
  msg.direction === "OUT";

// Content the bot can't read is stored as "[type]", Meta's message type: the ones a
// dispatcher should recognise get a label of their own, Meta's catch-alls a generic one
const MEDIA_TYPES = [
  "image",
  "audio",
  "video",
  "sticker",
  "document",
  "location",
  "contacts",
  "reaction",
] as const;
const OTHER_TYPES = ["unsupported", "interactive", "button", "order", "system"];
export type WhatsappMediaType = (typeof MEDIA_TYPES)[number] | "other";

/** The content an inbound "[type]" body stands for; undefined for the client's own text. */
export const whatsappMediaType = (body: string): WhatsappMediaType | undefined => {
  const type = /^\[([a-z_]+)\]$/.exec(body)?.[1] ?? "";
  if ((MEDIA_TYPES as readonly string[]).includes(type)) return type as WhatsappMediaType;
  return OTHER_TYPES.includes(type) ? "other" : undefined;
};

export type WhatsappMessageKind = "flowTemplate" | "flowReply" | "media" | "text";

/** Which bubble a dialogue message is drawn as. Everything the bot does not mark is text. */
export const whatsappMessageKind = (
  msg: Pick<ConnectProto.WhatsappMessage, "direction" | "body">,
): WhatsappMessageKind => {
  if (isOutgoing(msg)) return msg.body === FLOW_TEMPLATE_BODY ? "flowTemplate" : "text";
  if (msg.body.startsWith(FLOW_REPLY_PREFIX)) return "flowReply";
  return whatsappMediaType(msg.body) ? "media" : "text";
};
